import React, { useState, useRef } from 'react';
import api from '../auth/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ImageUpload Component
 * 
 * A reusable component for uploading images with drag-and-drop support,
 * preview, and validation.
 * 
 * Props:
 * - value: string - Current image URL
 * - onChange: (url: string) => void - Callback when image is uploaded
 * - maxSizeMB: number - Maximum file size in MB (default: 2)
 * - acceptedFormats: array - Accepted file formats (default: ['jpg', 'jpeg', 'png', 'webp'])
 */
const ImageUpload = ({
    value = '',
    onChange,
    maxSizeMB = 2,
    acceptedFormats = ['jpg', 'jpeg', 'png', 'webp']
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(value);
    const fileInputRef = useRef(null);

    const validateFile = (file) => {
        // Check file type
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!acceptedFormats.includes(fileExtension)) {
            return `Invalid file type. Accepted formats: ${acceptedFormats.join(', ')}`;
        }

        // Check file size
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > maxSizeMB) {
            return `File size exceeds ${maxSizeMB}MB limit`;
        }

        return null;
    };

    const uploadFile = async (file) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setUploading(true);

        try {
            // Create FormData
            const formData = new FormData();
            formData.append('file', file);

            // Upload to server
            const response = await api.post('/exercises/upload-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const imageUrl = response.data.image_url;
            setPreview(imageUrl);
            onChange(imageUrl);
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            uploadFile(files[0]);
        }
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            uploadFile(files[0]);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemove = () => {
        setPreview('');
        onChange('');
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="image-upload">
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFormats.map(f => `.${f}`).join(',')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {!preview ? (
                <div
                    className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleClick}
                >
                    {uploading ? (
                        <div className="upload-progress">
                            <div className="spinner"></div>
                            <p>Uploading...</p>
                        </div>
                    ) : (
                        <>
                            <div className="upload-icon">📁</div>
                            <p className="upload-text">
                                Drag and drop an image here, or click to select
                            </p>
                            <p className="upload-hint">
                                Accepted formats: {acceptedFormats.join(', ')} (max {maxSizeMB}MB)
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <div className="image-preview">
                    <img
                        src={preview.startsWith('http') ? preview : `${BACKEND_URL}${preview}`}
                        alt="Exercise preview"
                    />
                    <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemove}
                        title="Remove image"
                    >
                        ✕
                    </button>
                </div>
            )}

            {error && (
                <div className="upload-error">
                    {error}
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
